import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SearchStudent } from '../../_models/search-student/search-student.model';
import { environment } from '../../../environments/environment';
import { BaseService } from '../base/base.service';
import { Router } from '@angular/router';
import { SessionUserHashManagerService } from '../session-user-hash-manager/session-user-hash-manager.service';
import { Guid } from 'guid-typescript';
import { StudentOnlineSession } from '../../_models/student-online-sessions/student-online-session.model';

/** Optional learner-search filters, sent only when the `filters.learner-search` switch is on. */
export interface StudentSearchFilters {
  /** Brands to restrict to; empty means every brand the user may see. */
  companyIds?: number[];
  /** true = on a live booking, false = not on one, null = any. */
  hasActiveBooking?: boolean | null;
  /**
   * Key stages (the Orbit order's key stage, e.g. KS3) to restrict to; empty means any. Sending the key
   * at all also asks the API to return each learner's key stages for the Key stage column.
   */
  keyStages?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class StudentService extends BaseService {

  private endpoint: string = environment.apiUrl;

  constructor(
    private httpClient: HttpClient,
    protected hashManagerService: SessionUserHashManagerService
    ) {
    super(hashManagerService);
      this.endpoint += this.endpoint.endsWith("/") ? "" : "/";
    }

  /**
   * Learner search. `filters` carries the optional filters behind the `filters.learner-search` switch;
   * omit it (the default) and the request body is exactly the pre-filter shape, with no new keys.
   */
  public searchStudents(searchString: string, clientFilter: string, clientId: number, isUnallocated: boolean = null, allocationSearch: boolean, filters: StudentSearchFilters = null) {
    let request: any = {
      "searchString": searchString,
      "clientSearchString": clientFilter,
      "clientId": clientId,
      "isNTP": false,
      "isUnallocated": isUnallocated,
      "allocationSearch": allocationSearch
    };
    if (filters) {
      request.companyIds = filters.companyIds ?? [];
      request.hasActiveBooking = filters.hasActiveBooking ?? null;
      request.keyStages = filters.keyStages ?? [];
    }
    return this.httpClient.post(this.endpoint + 'api/Students/StudentSearch', 
    request,
    this.prepareOptions(request)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('searchStudents'))
    );
  }

  /**
   * The key stages on the caller's cached bookings (`GET api/Students/KeyStages`) — the options for the
   * learner search's key-stage filter. Called only while `filters.learner-search` is on. Any failure
   * resolves to an empty list, which leaves the filter with nothing to pick rather than breaking the page.
   */
  public getSearchKeyStages(): Observable<string[]> {
    return this.httpClient.get(this.endpoint + 'api/Students/KeyStages', this.prepareOptions(null)).pipe(
      map((data: any) => (data ? data.body : null) ?? []),
      catchError(() => of([] as string[]))
    );
  }

  public selectStudent(studentId: number) {
    return this.httpClient.post(this.endpoint + 'api/Students/StudentSelect', 
    { "StudentId": studentId },
    this.prepareOptions({ "StudentId": studentId })).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('selectStudent'))
    );
  }
  
  public insertStudent(student: SearchStudent) {
    return this.httpClient.post(this.endpoint + 'api/Students/StudentInsert', 
    student,
    this.prepareOptions(student)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('insertStudent'))
    );
  }
  
  public updateStudent(student: SearchStudent) {
    return this.httpClient.post(this.endpoint + 'api/Students/StudentUpdate', 
    student,
    this.prepareOptions(student)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('updateStudent'))
    );
  }
  
  public uploadStudent(fileBase64: string, clientId: number, createLogins: boolean) {
    return this.httpClient.post(this.endpoint + "api/Students/StudentUpload", 
    {"fileBase64": fileBase64, "ClientId": clientId, "CreateLogins": createLogins},
    this.prepareOptions({"fileBase64": fileBase64, "ClientId": clientId, "CreateLogins": createLogins})).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('uploadStudent'))
    );
  }
  
  public readUpcomingSessions(studentId: number) {
    return this.httpClient.post(this.endpoint + 'api/Students/ReadUpcomingSessions', 
    { "StudentId": studentId },
    this.prepareOptions({ "StudentId": studentId })).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('readUpcomingSessions'))
    );
  }

  public readPlacementInformation(studentId: number) {
    return this.httpClient.post(this.endpoint + 'api/Students/ReadPlacementInformation', 
    { "StudentId": studentId },
    this.prepareOptions({ "StudentId": studentId })).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('readPlacementInformation'))
    );
  }

  public launchDigitalClassroom(session: StudentOnlineSession) {
    return this.httpClient.post(this.endpoint + 'api/Students/LaunchDigitalClassroom', session,
    this.prepareOptions(session)).pipe(
      map((data: any) => {
        return data.body;
      }), catchError(this.handleError('launchDigitalClassroom'))
    );
  }
  public generateAccess(studentId: number, accessType: number, genToken?: string) {
    let request = { "StudentId": studentId, "AccessRequestType": accessType, "Token": genToken };
    return this.httpClient.post(this.endpoint + 'api/Students/GenerateAccess', 
    request,
    this.prepareOptions(request)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('generateAccess'))
    );
  }

  public checkToken(genToken: string) {
    return this.httpClient.post(this.endpoint + 'api/Students/CheckToken', 
    { "Token": genToken },
    this.prepareAnonymousOptions({ "Token": genToken })).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('checkToken'))
    );
  }

  public resetPin(email: string) {
    return this.httpClient.post(this.endpoint + 'api/Students/SendStudentLoginToken', 
    { "Email": email },
    this.prepareAnonymousOptions({ "Email": email })).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('resetPin'))
    );
  }

  public sendSignUpEmail(studentId: number) {
    return this.httpClient.post(this.endpoint + 'api/Students/SendSignUpEmail', 
    { "StudentId": studentId },
    this.prepareOptions({ "StudentId": studentId })).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('sendSignUpEmail'))
    );
  }

  public sendBulkSignUpEmail(studentIds: number[]) {
    return this.httpClient.post(this.endpoint + 'api/Students/SendSignUpEmailBulk', 
    { "StudentIds": studentIds },
    this.prepareOptions({ "StudentIds": studentIds })).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('sendSignUpEmail'))
    );
  }

  public emailLoginLink(studentId: number, webLinkUrl: string, email: string) {
    return this.httpClient.post(this.endpoint + 'api/Students/SendWebLinkEmail', 
    { "StudentId": studentId, "WebLinkUrl": webLinkUrl, "EmailAddress": email },
    this.prepareOptions({ "StudentId": studentId, "WebLinkUrl": webLinkUrl, "EmailAddress": email })).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('sendSignUpEmail'))
    );
  }

  public getUnassignedStudents() {
    // return this.httpClient.post(this.endpoint + 'api/Students/SendSignUpEmail', 
    // { "StudentId": studentId },
    // this.prepareOptions()).pipe(
    //   map((data: any) => {
        
    //     if (data.status === 299) {
    //       throw {
    //         message: data.body,
    //         status: data.status
    //       };
    //     } else {
    //       return data.body;
    //     }
    //   }), catchError(this.handleError('sendSignUpEmail'))
    // );

    return of([
      { name: "Sam", surname: "Test", subject: "Maths", year: "Year 6", studentId: "123"},
      { name: "Tom", surname: "Test", subject: "Maths", year: "Year 6", studentId: "234"},
      { name: "Alice", surname: "Test", subject: "Maths", year: "Year 6", studentId: "345"},
      { name: "Mark", surname: "Test", subject: "Maths", year: "Year 6", studentId: "567"},
      { name: "Ruth", surname: "Test", subject: "English", year: "Year 6", studentId: "789"},
      { name: "Henry", surname: "Test", subject: "English", year: "Year 7", studentId: "898"},
      { name: "Patrick", surname: "Test", subject: "English", year: "Year 7", studentId: "987"},
      { name: "Sarah", surname: "Test", subject: "Maths", year: "Year 7", studentId: "876"},
      { name: "Nicole", surname: "Test", subject: "German", year: "Year 8", studentId: "765"},
      { name: "Sophie", surname: "Test", subject: "Maths", year: "Year 9", studentId: "654"},
      { name: "Peter", surname: "Test", subject: "German", year: "Year 9", studentId: "543"},
      { name: "Matthew", surname: "Test", subject: "Maths", year: "Year 9", studentId: "432"},
      { name: "Paul", surname: "Test", subject: "Geography", year: "Year 10", studentId: "321"},
      { name: "Kim", surname: "Test", subject: "Geography", year: "Year 11", studentId: "211"},
      { name: "Natasha", surname: "Test", subject: "Maths", year: "Year 11", studentId: "121"},
      { name: "Kevin", surname: "Test", subject: "English", year: "Year 11", studentId: "456"},
      { name: "Susan", surname: "Test", subject: "Geography", year: "Year 10", studentId: "678"}
    ]);
  }

}
